/**
 * The Funnel tab of the Reports page: inquiries in, converted, waiting, and
 * turned away per Monday-to-Sunday week in the practice's time zone, as one
 * weekly-totals table and one table per breakdown (unit, county, referrer),
 * plus a CSV download of the same counts.
 *
 * Every number is a count. Each inquiry is counted in the week it arrived and
 * classified by where it stands today, so a week's numbers keep moving until
 * its inquiries have all converted or been turned away.
 *
 * FunnelReport owns the fetch (gated by the caller's capability check) and the
 * async chrome; FunnelTables renders a report it is handed, so a test can feed
 * it a fixture directly.
 */

import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi, type FunnelBreakdownWire, type FunnelWeekWire, type IntakeFunnelReportWire } from '@/lib/api';
import { AsyncBoundary } from '@/components/primitives/AsyncBoundary';
import { Button } from '@/components/primitives/Button';
import { formatWeekEnd } from '@/components/reports/operatingDerive';
import { buildFunnelCsv, funnelCsvFilename } from '@/components/reports/funnelCsv';

export const FUNNEL_WEEKS = 12;

const HEAD_ROW = 'text-left text-xs font-semibold uppercase tracking-wider text-sage-400 border-b border-sage-800/[8%]';
const NUM_CELL = 'py-2 px-3 text-right tabular-nums text-ink';

function weekLabel(weekStart: string, weekEnd: string): string {
  return `${formatWeekEnd(weekStart)} to ${formatWeekEnd(weekEnd)}`;
}

function PartialChip(): ReactNode {
  return (
    <span
      data-testid="funnel-partial"
      className="ml-2 inline-flex items-center rounded-full bg-sage-800/[6%] px-2 py-0.5 text-xs font-medium text-ink-soft"
    >
      partial
    </span>
  );
}

function CountHeaders(): ReactNode {
  return (
    <>
      <th className="py-2 px-3 text-right">Inquiries</th>
      <th className="py-2 px-3 text-right">Converted</th>
      <th className="py-2 px-3 text-right">Waiting</th>
      <th className="py-2 pl-3 text-right">Turned away</th>
    </>
  );
}

function CountCells({ row }: { row: { inquiries: number; converted: number; waiting: number; turned_away: number } }): ReactNode {
  return (
    <>
      <td className={`${NUM_CELL} font-semibold`}>{row.inquiries}</td>
      <td className={NUM_CELL}>{row.converted}</td>
      <td className={NUM_CELL}>{row.waiting}</td>
      <td className="py-2 pl-3 text-right tabular-nums text-ink">{row.turned_away}</td>
    </>
  );
}

function WeeklyTotalsTable({ weeks }: { weeks: FunnelWeekWire[] }): ReactNode {
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <h2 className="font-display text-lg text-sage-800 mb-3">By week</h2>
      <div className="overflow-x-auto">
        <table data-testid="funnel-by-week" className="w-full text-sm">
          <thead>
            <tr className={HEAD_ROW}>
              <th className="py-2 pr-3">Week</th>
              <CountHeaders />
            </tr>
          </thead>
          <tbody>
            {weeks.map((w) => (
              <tr key={w.week_start} data-testid="funnel-week-row" data-week={w.week_start} className="border-b border-sage-800/[5%] last:border-0">
                <td className="py-2 pr-3 text-ink whitespace-nowrap">
                  {weekLabel(w.week_start, w.week_end)}
                  {w.is_partial && <PartialChip />}
                </td>
                <CountCells row={w} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BreakdownTable({ breakdown, weeks }: { breakdown: FunnelBreakdownWire; weeks: FunnelWeekWire[] }): ReactNode {
  const weekByStart = new Map(weeks.map((w) => [w.week_start, w]));
  // Rows arrive grouped by week (newest first); the week cell spans its group.
  const groups: { week_start: string; rows: FunnelBreakdownWire['rows'] }[] = [];
  for (const r of breakdown.rows) {
    const last = groups[groups.length - 1];
    if (last && last.week_start === r.week_start) last.rows.push(r);
    else groups.push({ week_start: r.week_start, rows: [r] });
  }
  return (
    <div className="bg-paper border border-sage-800/[8%] rounded-card shadow-sh1 p-4">
      <h2 className="font-display text-lg text-sage-800 mb-3">By {breakdown.label.toLowerCase()}</h2>
      {breakdown.rows.length === 0 ? (
        <p className="text-sm text-ink-soft">Once you record intakes, you'll see inquiries here.</p>
      ) : (
        <div className="overflow-x-auto">
          <table data-testid={`funnel-table-${breakdown.dimension}`} className="w-full text-sm">
            <thead>
              <tr className={HEAD_ROW}>
                <th className="py-2 pr-3">Week</th>
                <th className="py-2 px-3">{breakdown.label}</th>
                <CountHeaders />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => {
                const week = weekByStart.get(g.week_start);
                return g.rows.map((r, idx) => (
                  <tr
                    key={`${r.week_start}:${r.key}`}
                    data-testid="funnel-row"
                    data-week={r.week_start}
                    data-key={r.key}
                    className="border-b border-sage-800/[5%] last:border-0"
                  >
                    {idx === 0 && (
                      <td rowSpan={g.rows.length} className="py-2 pr-3 text-ink whitespace-nowrap align-top">
                        {week ? weekLabel(week.week_start, week.week_end) : r.week_start}
                        {week?.is_partial && <PartialChip />}
                      </td>
                    )}
                    <td className="py-2 px-3 text-ink">{r.label}</td>
                    <CountCells row={r} />
                  </tr>
                ));
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function FunnelTables({ report }: { report: IntakeFunnelReportWire }): ReactNode {
  function downloadCsv(): void {
    const blob = new Blob([buildFunnelCsv(report)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = funnelCsvFilename(report);
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6" data-testid="funnel-section">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-soft">
          Each inquiry counts in the week it arrived (Monday to Sunday, {report.time_zone.replace('_', ' ')}) and is
          classified by where it stands today: converted, still waiting, or turned away.
        </p>
        <Button data-testid="funnel-download-csv" type="button" variant="secondary" size="sm" onClick={downloadCsv}>
          Download CSV
        </Button>
      </div>
      <WeeklyTotalsTable weeks={report.by_week} />
      {report.breakdowns.map((b) => (
        <BreakdownTable key={b.dimension} breakdown={b} weeks={report.by_week} />
      ))}
    </div>
  );
}

export function FunnelReport({ tenantSlug, enabled }: { tenantSlug: string; enabled: boolean }): ReactNode {
  const query = useQuery<IntakeFunnelReportWire>({
    queryKey: ['reports-funnel-weekly', tenantSlug, FUNNEL_WEEKS],
    queryFn: ({ signal }) => reportsApi.funnelWeekly(tenantSlug, { weeks: FUNNEL_WEEKS }, signal),
    staleTime: 60_000,
    enabled,
  });
  const data = query.data;
  const isEmpty = !!data && data.by_week.every((w) => w.inquiries === 0);
  return (
    <AsyncBoundary
      isLoading={enabled && query.isPending}
      isError={query.isError}
      error={query.error}
      onRetry={() => void query.refetch()}
      isEmpty={isEmpty}
      emptyHeadline={`No inquiries in the last ${FUNNEL_WEEKS} weeks`}
      emptyBody="Once you record intakes, you'll see inquiries here."
    >
      {data && !isEmpty ? <FunnelTables report={data} /> : null}
    </AsyncBoundary>
  );
}
